import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormError } from "@/components/ui/form-error";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NavigationStatus } from "@/components/utils/navigation-status";
import { ActionWrapper } from "@/utils/wretch";
import { zodResolver } from "@hookform/resolvers/zod";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useFetcher } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
    receiver: z.string().min(1, {
        message: "Vous devez spécifier un numéro de téléphone"
    }),
    text: z
        .string()
        .min(1, {
            message: "Vous devez spécifier un message"
        })
        .max(255, {
            message: "Le message ne peut pas dépasser 255 caractères"
        })
});

export function MessageDialog({ phone_number, open, setOpen, sf_object_id }: { phone_number?: string; sf_object_id?: string; open?: boolean; setOpen?: (open: boolean) => void }) {
    const fetcher = useFetcher<ActionWrapper<{}>>();
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            receiver: phone_number,
            text: ""
        }
    });

    useEffect(() => {
        form.reset({ receiver: phone_number, text: "" });
    }, [open, phone_number]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        const data = { ...values, sf_object_id: sf_object_id || null };
        fetcher.submit(data, { method: "post", action: "/outils/message", encType: "application/json" });
    };

    useEffect(() => {
        if (fetcher.data?.success) {
            toast.success("Le message a été envoyé, il apparaîtra dans la liste des activités d'ici quelques instants");
            setOpen?.(false);
        } else {
        }
    }, [fetcher.data]);

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[750px]">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)}>
                            <DialogHeader>
                                <DialogTitle>Send a SMS to {phone_number}</DialogTitle>
                            </DialogHeader>
                            <div className="py-4">
                                {fetcher.data?.success === false && <FormError errorMessage={fetcher.data.message} className="mb-4" />}
                                <div className="mb-5 grid grid-cols-12 gap-5 lg:grid-cols-12">
                                    <div className="col-span-12">
                                        <FormField
                                            control={form.control}
                                            name="receiver"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel required>Receiver</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="+33 6 XX XX XX XX" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className="col-span-12">
                                        <FormField
                                            control={form.control}
                                            name="text"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <div className="flex flex-row items-center justify-between">
                                                        <FormLabel required>Message</FormLabel>
                                                        <span className="text-xs text-muted-foreground">{form.watch("text").length} / 255 characters</span>
                                                    </div>
                                                    <FormControl>
                                                        <Textarea placeholder="Your message here..." autoFocus={Boolean(phone_number)} {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="submit" disabled={fetcher.state != "idle"}>
                                    <NavigationStatus onSubmitting="Sending..." fetcher={fetcher}>
                                        Send message
                                    </NavigationStatus>
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </>
    );
}
